
import { Component, Vue, Watch } from "vue-property-decorator";
import * as Model from "@gigalot/data-models";
import BreadcrumbTrail from "@/components/BreadcrumbTrail.vue";
import { homeBreadcrumbs } from "@/utils/breadcrumbs";

type JobDeliveryStatus = "notAllocated" | "allocated" | "delivered" | "partial" | "over" | "";

interface KraalBoardRow {
  active: boolean;
  actualFedWet: number;
  capacityPercent: number;
  currentRationDay?: number;
  currentRationDays?: number;
  daysInFeedlot?: number;
  deliveryPercent: number;
  deliveryStatus: JobDeliveryStatus;
  kgPerAnimalDry?: number;
  kgPerAnimalWet?: number;
  kraalAverageMass?: number;
  kraalGuid: string;
  kraalId: string;
  kraalType: string;
  numberAnimals: number;
  owner: string;
  projectedDaysInFeedlot?: number;
  ration: string;
  rationColour?: string;
  recommendedCapacity?: number;
  totalAllocatedDry: number;
  totalAllocatedWet: number;
}

@Component({
  components: {
    BreadcrumbTrail,
  },
})
export default class KraalBoard extends Vue {
  loading = false;
  errorText = "";
  search = "";
  selectedLineGuid = "";
  selectedKraalGuid = "";
  lines: Model.Line[] = [];
  feedPlanItems: Model.FeedingPlanItem[] = [];
  feedScheduleItems: Model.FeedingScheduleItem[] = [];
  rations: Model.Ration[] = [];
  owners: Model.CustomFeeder[] = [];

  mounted() {
    this.$store.dispatch("user/addFirebaseCallback", this.loadData);
  }

  get themeClass(): string {
    return this.$store.state.lightDarkMode === "dark" ? "kraal-board-grid--dark" : "kraal-board-grid--light";
  }

  get breadcrumbs() {
    return homeBreadcrumbs({ text: "Kraal Board", disabled: true });
  }

  get moment() {
    return this.$store.state.moment;
  }

  get lineOptions(): Model.Line[] {
    return [...this.lines].sort((left, right) => {
      if (left.active !== right.active) {
        return left.active ? -1 : 1;
      }

      return this.formatLineLabel(left).localeCompare(this.formatLineLabel(right), undefined, { numeric: true, sensitivity: "base" });
    });
  }

  get selectedLine(): Model.Line | undefined {
    return this.lines.find((line) => line.guid === this.selectedLineGuid);
  }

  get kraalRows(): KraalBoardRow[] {
    const selectedLine = this.selectedLine;
    if (!selectedLine || !Array.isArray(selectedLine.kraals)) return [];

    const sortedKraals = [...selectedLine.kraals].sort((left, right) =>
      (left.kraalId || "").localeCompare(right.kraalId || "", undefined, { numeric: true, sensitivity: "base" })
    );

    return sortedKraals.map((kraal) => {
      const planItem = this.findPlanItem(kraal);
      const scheduleItem = this.findScheduleItem(kraal);
      const rationGuid = planItem?.currentRation?.rationGuid || scheduleItem?.rationGuid;
      const rationName = planItem?.currentRation?.ration || scheduleItem?.ration || "";
      const dryMatterFactor = this.getDryMatterFactor(rationGuid, rationName);
      const numberAnimals = Number(planItem?.numberAnimals) || 0;
      const recommendedCapacity = Number(kraal.recommendedCapacity) || 0;
      const totalAllocatedWet = Number(scheduleItem?.feedAllocated) || 0;
      const actualFedWet = Number(scheduleItem?.actualFed) || 0;
      const totalAllocatedDry = totalAllocatedWet * dryMatterFactor;
      const kgPerAnimalWet = numberAnimals > 0 ? totalAllocatedWet / numberAnimals : undefined;
      const kgPerAnimalDry = numberAnimals > 0 ? totalAllocatedDry / numberAnimals : undefined;
      const capacityPercent = recommendedCapacity > 0 ? Math.min((numberAnimals / recommendedCapacity) * 100, 999) : 0;
      const deliveryPercent = totalAllocatedWet > 0 ? Math.min((actualFedWet / totalAllocatedWet) * 100, 999) : 0;

      return {
        active: kraal.active,
        actualFedWet,
        capacityPercent,
        currentRationDay: this.daysSince(planItem?.currentRation?.startDate),
        currentRationDays: planItem?.currentRation?.days,
        daysInFeedlot: this.daysSince(planItem?.kraalDate),
        deliveryPercent,
        deliveryStatus: this.getDeliveryStatus(totalAllocatedWet, actualFedWet),
        kgPerAnimalDry,
        kgPerAnimalWet,
        kraalAverageMass: planItem?.kraalAverageMass,
        kraalGuid: kraal.guid,
        kraalId: kraal.kraalId,
        kraalType: kraal.kraalType || "",
        numberAnimals,
        owner: this.lookupOwner(planItem?.owner),
        projectedDaysInFeedlot: planItem?.projectedDaysInFeedlot,
        ration: rationName,
        rationColour: planItem?.currentRation?.colour,
        recommendedCapacity: kraal.recommendedCapacity,
        totalAllocatedDry,
        totalAllocatedWet,
      };
    });
  }

  get filteredRows(): KraalBoardRow[] {
    const search = this.search.trim().toLowerCase();
    if (!search) return this.kraalRows;

    return this.kraalRows.filter((row) => {
      const haystack = [row.kraalId, row.kraalType, row.owner, row.ration].join(" ").toLowerCase();
      return haystack.includes(search);
    });
  }

  get totalAnimals(): number {
    return this.filteredRows.reduce((total, row) => total + row.numberAnimals, 0);
  }

  get averageCapacityFill(): number {
    if (!this.filteredRows.length) return 0;
    const total = this.filteredRows.reduce((sum, row) => sum + row.capacityPercent, 0);
    return total / this.filteredRows.length;
  }

  get statusLegend() {
    return [
      { key: "notAllocated" as JobDeliveryStatus, label: "Not allocated" },
      { key: "allocated" as JobDeliveryStatus, label: "Job allocated" },
      { key: "delivered" as JobDeliveryStatus, label: "Correct delivery" },
      { key: "partial" as JobDeliveryStatus, label: "Partial delivery" },
      { key: "over" as JobDeliveryStatus, label: "Over delivery" },
    ];
  }

  @Watch("selectedLineGuid")
  onSelectedLineGuidChanged(nextValue: string) {
    const currentValue = typeof this.$route.query.line === "string" ? this.$route.query.line : "";
    if (nextValue !== currentValue) {
      const nextQuery = { ...this.$route.query };
      if (nextValue) {
        nextQuery.line = nextValue;
      } else {
        delete nextQuery.line;
      }
      this.$router.replace({ path: this.$route.path, query: nextQuery }).catch(() => undefined);
    }

    if (!this.filteredRows.some((row) => row.kraalGuid === this.selectedKraalGuid)) {
      this.selectedKraalGuid = this.filteredRows[0]?.kraalGuid || "";
    }
  }

  toggleSelectedKraal(kraalGuid: string) {
    this.selectedKraalGuid = this.selectedKraalGuid === kraalGuid ? "" : kraalGuid;
  }

  initializeSelectedLine() {
    const routeLineGuid = typeof this.$route.query.line === "string" ? this.$route.query.line : "";
    const hasRouteMatch = routeLineGuid && this.lineOptions.some((line) => line.guid === routeLineGuid);

    if (hasRouteMatch) {
      this.selectedLineGuid = routeLineGuid;
    } else if (!this.selectedLineGuid || !this.lineOptions.some((line) => line.guid === this.selectedLineGuid)) {
      this.selectedLineGuid = this.lineOptions[0]?.guid || "";
    }

    if (!this.filteredRows.some((row) => row.kraalGuid === this.selectedKraalGuid)) {
      this.selectedKraalGuid = this.filteredRows[0]?.kraalGuid || "";
    }
  }

  formatLineLabel(line: Model.Line): string {
    if (line.displayName && line.code) {
      return `${line.displayName} (${line.code})`;
    }
    return line.displayName || line.code || "Unnamed line";
  }

  formatDecimal(value?: number, decimals: number = 2): string {
    if (value === undefined || value === null || Number.isNaN(value)) return "";
    return Number(value).toFixed(decimals);
  }

  formatDayPair(current?: number, total?: number): string {
    if (current === undefined && total === undefined) return "-";
    if (current !== undefined && total !== undefined) return `${current}/${total}`;
    if (current !== undefined) return `${current}`;
    return `-/${total}`;
  }

  rationPillStyle(colour?: string) {
    if (!colour) return {};

    return {
      backgroundColor: colour.toLowerCase(),
      color: "#111827",
    };
  }

  statusClass(status: JobDeliveryStatus): string {
    return status ? `kraal-board-status--${status}` : "";
  }

  statusLabel(status: JobDeliveryStatus): string {
    switch (status) {
      case "allocated":
        return "Allocated";
      case "delivered":
        return "Delivered";
      case "partial":
        return "Partial";
      case "over":
        return "Over";
      case "notAllocated":
        return "Pending";
      default:
        return "-";
    }
  }

  deliveryProgressColor(status: JobDeliveryStatus): string {
    switch (status) {
      case "allocated":
        return "light-blue lighten-2";
      case "delivered":
        return "green lighten-1";
      case "partial":
        return "orange lighten-1";
      case "over":
        return "red lighten-1";
      default:
        return "grey lighten-1";
    }
  }

  deliveryBarStyle(row: KraalBoardRow) {
    if (row.actualFedWet <= 0) {
      return {
        height: "0%",
      };
    }

    const height = Math.max(6, Math.min(row.deliveryPercent, 100));
    return {
      height: `${height}%`,
    };
  }

  daysSince(startDate?: number): number | undefined {
    if (!startDate) return undefined;
    const start = this.moment(startDate).startOf("day");
    const today = this.moment().startOf("day");
    return Math.max(today.diff(start, "days") + 1, 1);
  }

  lookupOwner(owner?: string): string {
    if (!owner) return "";
    const ownerLookup = this.owners.find((item) => item.guid === owner);
    return ownerLookup?.name || owner;
  }

  findPlanItem(kraal: Model.Kraal): Model.FeedingPlanItem | undefined {
    return this.feedPlanItems.find((item) => item.kraalGuid === kraal.guid || item.kraalId === kraal.kraalId);
  }

  findScheduleItem(kraal: Model.Kraal): Model.FeedingScheduleItem | undefined {
    return this.feedScheduleItems.find((item) => item.kraalGuid === kraal.guid || item.kraalId === kraal.kraalId);
  }

  getDryMatterFactor(rationGuid?: string, rationName?: string): number {
    let ration: Model.Ration | undefined;

    if (rationGuid) {
      ration = this.rations.find((item) => item.guid === rationGuid);
    }

    if (!ration && rationName) {
      ration = this.rations.find((item) => item.name === rationName);
    }

    return ration?.dryMatterFactor?.dryMatterFactor || 1;
  }

  getDeliveryStatus(feedAllocated: number, actualFed: number): JobDeliveryStatus {
    const tolerance = 0.5;

    if (feedAllocated <= tolerance && actualFed <= tolerance) {
      return "notAllocated";
    }

    if (actualFed > feedAllocated + tolerance) {
      return "over";
    }

    if (feedAllocated > tolerance && actualFed >= feedAllocated - tolerance) {
      return "delivered";
    }

    if (actualFed > tolerance) {
      return "partial";
    }

    if (feedAllocated > tolerance) {
      return "allocated";
    }

    return "notAllocated";
  }

  async loadData() {
    this.loading = true;
    this.errorText = "";

    try {
      const gql = `query KraalBoard($guid: String!) {
        Lines(guid: $guid) {
          guid
          code
          displayName
          active
          kraals {
            guid
            kraalId
            active
            kraalType
            recommendedCapacity
          }
        }
        FeedPlan(guid: $guid) {
          FeedingPlanItems {
            guid
            kraalId
            kraalGuid
            numberAnimals
            owner
            kraalAverageMass
            projectedDaysInFeedlot
            kraalDate
            currentRation {
              ration
              rationGuid
              startDate
              colour
              days
            }
          }
        }
        FeedSchedule(guid: $guid) {
          feedingScheduleItems {
            guid
            kraalId
            kraalGuid
            ration
            rationGuid
            feedAllocated
            actualFed
          }
        }
        Rations(guid: $guid) {
          guid
          name
          dryMatterFactor {
            dryMatterFactor
          }
        }
        customFeeders(guid: $guid) {
          guid
          name
          active
        }
      }`;

      const response = await this.$store.dispatch("graphQl", {
        gql,
        variables: { guid: this.$store.state.user.location.guid },
      });

      this.lines = (response?.data?.Lines ?? []) as Model.Line[];
      this.feedPlanItems = (response?.data?.FeedPlan?.FeedingPlanItems ?? []) as Model.FeedingPlanItem[];
      this.feedScheduleItems = (response?.data?.FeedSchedule?.feedingScheduleItems ?? []) as Model.FeedingScheduleItem[];
      this.rations = (response?.data?.Rations ?? []) as Model.Ration[];
      this.owners = (response?.data?.customFeeders ?? []) as Model.CustomFeeder[];

      this.initializeSelectedLine();
    } catch (err) {
      console.log("loadData -> error:", err);
      this.errorText = `Error downloading kraal board data: ${err}`;
    } finally {
      this.loading = false;
    }
  }
}
